import { Button, Popover, Select } from 'antd';
import React, { useEffect, useState } from 'react';
import Icon, {
  CheckOutlined,
  CloseOutlined,
  RightOutlined,
} from '@ant-design/icons';
import SelectKPIFilterDate from './SelectKPIFilterDate';
import { useGlobalState } from './GlobalState';
import { Link, useHistory } from 'react-router-dom';
import dayjs from 'dayjs';

import '../assets/css/dashboard-metrics.css';
import SvgDateRangePicker from '../../../../../admin/assets/images/Dashboard/DateRangePicker';
import SvgTrendDownArrow from '../../../../../admin/assets/images/Dashboard/TrendDownArrow';
import SvgTrendUpArrow from '../../../../../admin/assets/images/Dashboard/TrendUpArrow';
import SvgOnlineBookings from '../../../../../admin/assets/images/Dashboard/OnlineBookings';
import SvgAdminBookings from '../../../../../admin/assets/images/Dashboard/AdminBookings';
import SvgCancellations from '../../../../../admin/assets/images/Dashboard/Cancellations';
// import SvgTotalRevenue from '../../../assets/images/Dashboard/TotalRevenue';

import {
  useGetDashboardDataMutationMutation,
  useGetDashboardDataQuery,
} from '../../calendar/eventAdminCalendarSlice';
import StatsCard from '../../stats/components/StatsCard';

const dateRangeFilters = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'week', label: 'Last 7 Days' },
  { value: 'month', label: 'Month-to-date' },
  { value: 'last_month', label: 'Last 30 Days' },
  { value: 'all', label: 'All Time' },
  { value: 'custom', label: 'Custom' },
];

export function StatsDateRangeSelect({
  initialValue = 'today',
  onFilterSelect,
  showAllTime = false,
}) {
  const [selected, setSelected] = useState(
    initialValue ?? dateRangeFilters[0].value
  );
  const [showFilterCalendar, setShowFilterCalendar] = useState(false);

  const handleFilterSelection = (value) => {
    setSelected(value);
    setShowFilterCalendar(Boolean(value === 'custom'));
    onFilterSelect(value);
  };

  return (
    <>
      <Popover
        open={showFilterCalendar}
        onOpenChange={setShowFilterCalendar}
        trigger='none'
        placement='bottomRight'
        content={
          <>
            <div className='row-right'>
              <Button
                icon={<CloseOutlined />}
                type='text'
                onClick={() => setShowFilterCalendar(false)}
              />
            </div>
            <div className='grid-center' id='filter-date-range'>
              <SelectKPIFilterDate />
            </div>
          </>
        }
      >
        <Select
          options={dateRangeFilters.filter(
            (e) => showAllTime || e?.value !== 'all'
          )}
          value={selected}
          className='date-range-selector gSemiBold fs-16'
          popupClassName='date-range-selector__list gRegular'
          onSelect={(value) => handleFilterSelection(value)}
          dropdownMatchSelectWidth={false}
          placement='bottomRight'
          bordered={false}
          suffixIcon={
            <Icon
              component={() => <SvgDateRangePicker />}
              className='gSemiBold black fs-18 pd-left-20'
              style={{
                pointerEvents: 'none' /* Allows icon to be selectable */,
              }}
            />
          }
          menuItemSelectedIcon={<CheckOutlined />}
        />
      </Popover>
    </>
  );
}

export const getTrendIcon = (trend, invertColours = false) => {
  let colours = ['#60BF97', '#CC3D3D'];
  if (invertColours) {
    colours = colours.reverse();
  }
  if (trend === 'incr') {
    return <SvgTrendUpArrow style={{ color: colours[0] }} />;
  }
  if (trend === 'decr') {
    return <SvgTrendDownArrow style={{ color: colours[1] }} />;
  }
  return null;
};

function AdminDashboardMetrics() {
  const history = useHistory();

  const [kpis, setKpis] = useGlobalState('kpis');
  const [loading, setLoading] = useState(false);

  const today = dayjs().format('YYYY-MM-DD');

  const { data: dashboardData, refetch } = useGetDashboardDataQuery({
    start: today,
    end: today,
  });

  const [getDashboardData] = useGetDashboardDataMutationMutation();

  useEffect(() => {
    setKpis(dashboardData);
  }, [dashboardData]);

  useEffect(() => {
    refetch();
  }, []);

  const getFilteredData = (val) => {
    console.log(val);
    let startDate = today;
    let endDate = today;
    if (val === 'yesterday') {
      startDate = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
    } else if (val === 'week') {
      startDate = dayjs().subtract(7, 'day').format('YYYY-MM-DD');
    } else if (val === 'month') {
      startDate = dayjs().startOf('month').format('YYYY-MM-DD');
    } else if (val === 'last_month') {
      startDate = dayjs().subtract(30, 'day').format('YYYY-MM-DD');
    }

    getDashboardData({ start: startDate, end: endDate })
      .unwrap()
      .then((res) => {
        setKpis(res);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const bookingsStats = [
    {
      title: 'Online Bookings',
      icon: <SvgOnlineBookings />,
      value: kpis?.online_bookings?.value || 0,
      trend: kpis?.online_bookings?.trend,
    },
    {
      title: 'Admin Bookings',
      icon: <SvgAdminBookings />,
      value: kpis?.admin_bookings?.value || 0,
      trend: kpis?.admin_bookings?.trend,
    },
    {
      title: 'Cancellations',
      icon: <SvgCancellations />,
      value: kpis?.cancellations?.value || 0,
      trend: kpis?.cancellations?.trend,
      invertColours: true,
    },
  ];

  return (
    <div className='admin-dashboard-metrics'>
      <div className='admin-dashboard-metrics__header mg-bottom-5'>
        <h1 className='gSemiBold mg-bottom-0'>Statistics</h1>
        <div className='admin-dashboard-metrics__date-filter'>
          <StatsDateRangeSelect
            onFilterSelect={(selectedRange) => getFilteredData(selectedRange)}
          />
        </div>
      </div>
      <div className='event_admin-dashboard-metrics__cards'>
        {bookingsStats.map((stat) => (
          <StatsCard
            key={stat.title}
            title={stat.title}
            value={stat.value}
            trend={stat.trend}
            icon={stat?.icon}
            invertColours={stat?.invertColours}
            loading={loading}
          />
        ))}
      </div>
      <Link to={`/event_admin/stats`}>
        <Button className='gSemiBold sb-blue-bg-600 white br10 mg-top-20 mg-bottom-20 pd-20 pd-top-10 pd-bottom-10 antd-link-button-override h-fit fs-16 w-fit row-center justify-center'>
          View More <RightOutlined />
        </Button>
      </Link>
    </div>
  );
}

export default AdminDashboardMetrics;
